/**
 * MapboxMain — the interactive map at the heart of the zone details page.
 *
 * Owned by Pages/ZonesDetails.jsx, which passes in the zone, its filtered
 * locations and all selection state (this component keeps only map-local
 * state such as viewport, draw mode and modal visibility).
 *
 * Responsibilities:
 *  - render locations as markers, clustered via use-supercluster for
 *    performance (zones can hold thousands of points)
 *  - render the zone circle + radius line ("addresses" menu) or the
 *    sub-zone polygons ("sub-zones" menu)
 *  - location popup with actions: edit address, set sub-zone start/stop
 *  - draw/edit sub-zone polygons (DrawControl) and open the Location /
 *    SubZone / ConfirmAddress modals
 *
 * Coordinate convention: Mapbox/GeoJSON uses [lng, lat] arrays while our
 * API objects use { lat, lng } — take care when converting.
 */
import React, { useRef, useState, useEffect, useMemo } from "react";
import Map, { Marker, NavigationControl, Popup } from "react-map-gl";
import { Source, Layer } from "react-map-gl";
import useSupercluster from "use-supercluster";
import { Button, Form, Modal } from "antd";
import { useSelector, useDispatch } from "react-redux";
import * as turf from "@turf/turf";
import bbox from "@turf/bbox";
import { sample, find, debounce } from "lodash";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { SyncOutlined } from '@ant-design/icons';

import "./Main.scss";
import "mapbox-gl/dist/mapbox-gl.css";
import markerIcons from "components/Markers/markerIcons";
import {
  EditAddressIcon,
  LocationCenterIcon,
} from "components/Icons";
import { PanToolIcon, PolylineIcon, CopyIcon } from "components/Icons";
import LocationForm from "components/Forms/Locations/Location";
import SubZoneForm from "components/Forms/SubZones/SubZone";
import { COLOR_DEFAULT, COLORS_SUBZONE } from "constants";
import { STATUS_BY_KEY } from "utils/locationStatus";

import DrawControl from "./DrawControl";
import ConfirmAddress from "./ConfirmAddress";

import { doUpdateSubZone } from "modules/subZone/stores/thunk";
import { doDetailZone } from "modules/zone/stores/thunk";

export const MAP_CONFIG = {
  mapboxApiAccessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
};

export const MAP_STYLES = {
  standard: "mapbox://styles/geofix/cm4iwp1r3003t01sif6ht7nsp"/*"mapbox://styles/theprof/cl8nwpgtl004z15mi7tjvhoaf"*/,
  satellite: "mapbox://styles/mapbox/satellite-streets-v12"
};

const navControlStyle = {
  right: 100,
  top: 200,
};

const MapboxMain = ({
  zone,
  circle,
  line,
  locations,
  locationClicked,
  setLocationClicked,
  subZoneClicked,
  setSubZoneClicked,
  subZonesChecked,
  menuSelected,
  setStopsInNewZones,
  stopsInNewZones,
  isConfirmedAddress,
  onSetIsConfirmedAddress,
}) => {
  const mapRef = useRef();
  const [fitBounded, setFitBounded] = useState(false);
  const [initialPoints, setInitialPoints] = useState([]);
  const [showDraw, setShowDraw] = useState(false);
  const [copied, setCopied] = useState({});
  const [style, setStyle] = useState(MAP_STYLES.standard);
  const [viewport, setViewport] = useState({
    width: "100%",
    height: "100%",
    zoom: 10,
  });
  const dispatch = useDispatch();

  // GF-34: a location without coordinates has no marker. Its popup and the
  // camera fly-to anchor at the zone center instead, so the address stays
  // reachable for editing without a fabricated position on the map.
  const anchorOf = (l) =>
    l.noLatLng
      ? { lng: zone.centerLng, lat: zone.centerLat }
      : { lng: l.lng, lat: l.lat };

  // Locations -> GeoJSON features for the clusterer. In "sub-zones" mode
  // only locations in checked sub-zones (or in none) are shown; in other
  // modes all passed-in locations are shown. Locations without coordinates
  // are dropped here (GF-34) — they would otherwise reach supercluster as
  // [null, null].
  const points = useMemo(
    () =>
      locations
        ?.filter((l) => !l.noLatLng)
        ?.filter((l) =>  {
          const isSubZoneSelected = menuSelected === "sub-zones" && (!!subZonesChecked.find(sc => sc._id === l.subZone?._id) || !l.subZone);
          const isMenuNotSubZone = menuSelected !== "sub-zones";
          return isSubZoneSelected || isMenuNotSubZone
        })
        ?.map(({ _id, orderNo, lat, lng, type, color, isStart, isEnd, subZone }) => ({
          type: "Feature",
          properties: {
            cluster: false,
            orderNo,
            locationType: type,
            locationColor: color,
            locationId: _id,
            isStart: isStart,
            isEnd: isEnd,
            subZone
          },
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
        })),
    [locations, menuSelected, subZonesChecked]
  );

  const bounds = mapRef.current
    ? mapRef.current.getMap().getBounds().toArray().flat()
    : null;

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom: viewport.zoom,
    options: { radius: 20, maxZoom: 20,
      // map: props => ({
      //   isStart: props.isStart,
      //   isEnd: props.isEnd
      // }),
      // reduce: (acc, props) => {
      //   acc.isStart |= props.isStart;
      //   acc.isEnd |= props.isEnd;
      //   return acc;
      // }
      /*reduce: (acc, props) => {
      if (props.isStart) {
        acc.isStart = true;
      }
      if (props.isEnd) {
        acc.isEnd = true;
      }
    }*/},
  });

  const [form] = Form.useForm();
  const [formSub] = Form.useForm();

  const { isFetching } = useSelector((state) => state.location);
  const { isFetching: isFetchingSub } = useSelector((state) => state.subZone);
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    location: null,
  });

  const [stateSub, setStateSub] = useState({
    visibleModal: false,
    action: "",
    subZone: null,
  });

  // One-time initial camera fit around the zone's locations (fitBounded
  // latch prevents re-zooming on every data refresh).
  useEffect(() => {
    if (locations.length > 2 && mapRef.current && !fitBounded) {
      setFitBounded(true);
      // Filter locations with ptsWithin property
      const validLocations = locations.filter((l) => l.ptsWithin);
      if (validLocations.length >= 2) {
        const points = validLocations.map((l) => [l.lng, l.lat]);
        const feature = turf.lineString(points);
        const [minLng, minLat, maxLng, maxLat] = bbox(feature);
        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 100, duration: 500 }
        );
      }
    }
  }, [locations, fitBounded]);

  useEffect(() => {
    if (locationClicked) {
      const { lng, lat } = anchorOf(locationClicked);
      mapRef.current.flyTo({
        center: [lng, lat],
        duration: 500,
      });
    }
    // Fly only when the clicked location changes — not when `zone` (read by
    // anchorOf) is refetched while a popup is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationClicked]);

  useEffect(() => {
    if (subZoneClicked) {
      formSub.resetFields();
      const params = {
        ...subZoneClicked,
        startAddress: {
          address: subZoneClicked.startAddress,
          latLng: {
            lat: subZoneClicked.startLat,
            lng: subZoneClicked.startLng,
          },
        },
        endAddress: {
          address: subZoneClicked.endAddress,
          latLng: { lat: subZoneClicked.endLat, lng: subZoneClicked.endLng },
        },
      };
      if (subZoneClicked.points.length > 0) {
        mapRef.current.flyTo({
          center: subZoneClicked.points[0],
          duration: 500,
        });
      }
      formSub.setFieldsValue(params);
      setStateSub({
        ...stateSub,
        action: "edit",
        subZone: subZoneClicked,
        visibleModal: true,
      });
    }
    // eslint-disable-next-line
  }, [subZoneClicked]);

  const onClosePopup = () => {
    setLocationClicked(null);
    onSetIsConfirmedAddress(false);
  };

  const handleCancel = () => {
    setState({ ...state, action: "", location: null, visibleModal: false });
    form.resetFields();
  };

  const changeStyleMap = () => {
    if (style === MAP_STYLES.standard) {
      setStyle(MAP_STYLES.satellite);
    } else {
      setStyle(MAP_STYLES.standard)
    }
  }

  const handleCancelSub = () => {
    setStateSub({
      ...stateSub,
      action: "",
      subZone: null,
      visibleModal: false,
    });
    formSub.resetFields();
    setShowDraw(false);
    setSubZoneClicked(null);
    setStopsInNewZones(undefined);
    setInitialPoints([]);
  };

  // Popup button dispatcher. "set_start_address"/"set_end_address" write the
  // clicked location's address onto its sub-zone (route start/stop) and
  // refresh the zone; every other action opens the LocationForm modal.
  const handleClickAction = (item, action) => {
    if (action === "set_start_address" || action === "set_end_address") {
      const subZone = item.subZone;
      if (subZone) {
        let params = action === "set_start_address" ? {
          startAddress: item.address,
          startLat: item.lat,
          startLng: item.lng
        } : {
          endAddress: item.address,
          endLat: item.lat,
          endLng: item.lng
        };
        dispatch(doUpdateSubZone({ id: subZone._id, ...params }));
        dispatch(doDetailZone(subZone.zone));
        setLocationClicked(null);
      }
    } else {
      form.resetFields();
      if (action === "new") {
        setState({
          ...state,
          action: action,
          location: null,
          visibleModal: true,
        });
      } else {
        const params = {
          ...item,
          currentAddress: item.address,
          location: {
            address: item.address,
            latLng: { lat: item.lat, lng: item.lng },
          },
        };
        form.setFieldsValue(params);
        setState({
          ...state,
          action: action,
          location: item,
          visibleModal: true,
        });
      }
    }
  };

  // Called whenever the drawn polygon changes (create/update/delete): counts
  // the locations inside it (shown as "stops" in the sub-zone form) and
  // stores the outline in the form's hidden `points` field for submission.
  const onCreate = async (e) => {
    const points = e.features[0].geometry.coordinates[0];
    const polygon = turf.polygon([points]);
    const locationInside = locations
      ?.filter(({ noLatLng }) => !noLatLng)
      ?.filter((l) => {
        const point = turf.point([l.lng, l.lat]);
        return turf.booleanPointInPolygon(point, polygon);
      });
    setStopsInNewZones && setStopsInNewZones(locationInside.length);
    formSub.setFieldsValue({ points });
  };

  const handleShowDraw = (showDrawT) => {
    setShowDraw(showDrawT);
    if (showDrawT) {
      formSub.setFieldsValue({ color: sample(COLORS_SUBZONE) });
      setStateSub({ ...stateSub, action: "new", subZone: null });
    } else {
      setInitialPoints([]);
    }
  };

  // "Edit area" from the sub-zone modal: closes the modal and re-opens the
  // draw tool pre-loaded with the sub-zone's existing polygon.
  const editPoints = () => {
    try {
      // console.log("Edit points!");
      setStateSub({
        ...stateSub,
        action: "edit",
        subZone: subZoneClicked,
        visibleModal: false,
      });
      // formSub.resetFields();
      // formSub.setFieldsValue({ points });
      // console.log("subZoneClicked:", subZoneClicked);
      setInitialPoints(subZoneClicked.points);
      setShowDraw(true);
      // setStopsInNewZones(undefined);
    } catch(e) {
      // console.log("mapRef.current", mapRef.current);
      console.error("Failed to editPoints", e);
    }
  }

  // Checked sub-zones as GeoJSON for the map's line/fill layers. The
  // numeric `color` property indexes into the paint "stops" tables below.
  const getFeatureLineSubZones = () => {
    return {
      type: "FeatureCollection",
      features: subZonesChecked.map((s, index) => {
        const f = turf.lineString(s.points);
        const properties = {
          ...f.properties,
          color: index,
        };
        return { ...f, properties };
      }),
    };
  };

  const getFeatureFillSubZones = () => {
    return {
      type: "FeatureCollection",
      features: subZonesChecked.map((s, index) => {
        const f = turf.polygon([[...s.points]]);
        const properties = {
          ...f.properties,
          color: index,
        };
        return { ...f, properties };
      }),
    };
  };

  const handleClusterClick = (cluster) => {
    const [longitude, latitude] = cluster.geometry.coordinates;
    const expansionZoom = Math.min(
      Math.max(supercluster.getClusterExpansionZoom(cluster.id), 12),
      20
    );
    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom: expansionZoom,
      duration: 500,
    });
  };

  return (
    <>
      <div className="MapboxMain">
        <Map
          ref={mapRef}
          initialViewState={{
            ...viewport,
            latitude: zone.centerLat,
            longitude: zone.centerLng,
          }}
          mapStyle={style}
          {...MAP_CONFIG}
          onMove={(event) => {
            debounce(
              (e) => {
                setViewport({ ...viewport, ...(e?.viewState || {}) });
              },
              100,
              { leading: true, maxWait: 1000 }
            )(event);
          }}
          // maxZoom={20}
        >
          <Source
            id="fill"
            type="geojson"
            data={
              menuSelected === "addresses" ? circle : getFeatureFillSubZones()
            }
          >
            <Layer
              type="fill"
              paint={{
                "fill-color":
                  menuSelected === "addresses"
                    ? "#55a2e7"
                    : {
                        property: "color",
                        stops: (() => {
                          if (subZonesChecked.length) {
                            return [
                              ...subZonesChecked.map((s, index) => [
                                index,
                                `#${s.color || COLOR_DEFAULT}`,
                              ]),
                            ];
                          } else {
                            return [[0, '#' + COLOR_DEFAULT]]
                          }
                        })(),
                      },
                "fill-opacity": 0.2,
              }}
            />
          </Source>

          <Source
            id="line"
            type="geojson"
            data={
              menuSelected === "addresses" ? line : getFeatureLineSubZones()
            }
          >
            <Layer
              type="line"
              paint={{
                "line-color":
                  menuSelected === "addresses"
                    ? "#55a2e7"
                    : {
                        property: "color",
                        stops: (() => {
                          if (subZonesChecked.length) {
                            return [
                              ...subZonesChecked.map((s, index) => [
                                index,
                                `#${s.color || COLOR_DEFAULT}`,
                              ]),
                            ];
                          } else {
                            return [[0, '#' + COLOR_DEFAULT]]
                          }
                        })(),
                      },
                "line-width": 1,
              }}
            />
          </Source>

          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const {
              cluster: isCluster,
              point_count: pointCount,
              locationId,
              locationType,
              locationColor,
              isStart,
              isEnd,
              orderNo,
            } = cluster.properties;

            if (isCluster) {
              const clusterMarkerSize = parseInt(pointCount / 300, 10);
              return (
                <Marker
                  key={`cluster-${cluster.id}`}
                  latitude={latitude}
                  longitude={longitude}
                  onClick={() => handleClusterClick(cluster)}
                >
                  <div
                    className="cluster-marker"
                    style={{
                      width: `${26 + clusterMarkerSize}px`,
                      height: `${26 + clusterMarkerSize}px`,
                    }}
                  >
                    {pointCount}
                  </div>
                </Marker>
              );
            }

            // const urlIcon = markerIcons[(isStart || isEnd) ? (isStart ? "start" : "end") : locationType][locationColor];
            const urlIcon = markerIcons[locationType][locationColor];
            return (
              <Marker
                key={locationType + locationId}
                latitude={latitude}
                longitude={longitude}
                style={{
                  zIndex:
                    locationClicked?._id === cluster.properties._id
                      ? orderNo * 0.00001
                      : 0,
                }}
              >
                <div
                  className={"marker " + ((isStart ? "isStart" : "") + " " + (isEnd ? "isEnd" : ""))}
                  onClick={() =>
                    setLocationClicked(find(locations, { _id: locationId }))
                  }
                >
                  <img src={urlIcon} alt="" />
                  {orderNo > 0 && (
                    <div
                      className="marker-info"
                      style={{
                        color: "white",
                        fontSize: "10px",
                        fontWeight: "bold",
                      }}
                    >
                      {orderNo}
                    </div>
                  )}
                </div>
              </Marker>
            );
          })}
          {locationClicked && !isConfirmedAddress && (
            <Popup
              key={`location-${locationClicked._id}`}
              longitude={anchorOf(locationClicked).lng}
              latitude={anchorOf(locationClicked).lat}
              closeOnClick={false}
              onClose={onClosePopup}
              anchor="bottom"
              offset={16}
            >
              <div className="location-popup">
                <div
                  className={`name ${locationClicked.orderNo === 0 && "zone"}`}
                >
                  <div className="left">
                    {/*
                      Samma formregel som adresslistan: streckad ring betyder
                      att adressen saknar position. Den måste gälla här också
                      — en adress utan position når popupen via listan, och
                      utan regeln blir den heldragen precis där grått och gult
                      annars bara skiljs av färgen.
                    */}
                    <div
                      className="icon"
                      style={{
                        border: `1px ${
                          STATUS_BY_KEY[locationClicked.status]?.dashed ? "dashed" : "solid"
                        } #${locationClicked.color}`,
                        color: `#${locationClicked.color}`,
                      }}
                    >
                      {locationClicked.orderNo !== 0 ? (
                        locationClicked.orderNo
                      ) : (
                        <LocationCenterIcon />
                      )}
                    </div>
                    {locationClicked.address}
                    <CopyToClipboard
                      text={locationClicked.address}
                      onCopy={() =>
                        setCopied({ ...copied, [locationClicked._id]: true })
                      }
                      className={`CopyToClipboard ${
                        copied[locationClicked._id] && "Copied"
                      }`}
                    >
                      <CopyIcon />
                    </CopyToClipboard>
                  </div>
                  <div className="right">{locationClicked.freeTextField1}</div>
                </div>
                {locationClicked.orderNo !== 0 && (
                  <>
                    <div className="notes">{locationClicked.note}</div>
                    <div className="actions">
                      <Button
                        className="btn-action"
                        onClick={() =>
                          handleClickAction(locationClicked, "enter_address")
                        }
                      >
                        Redigera adress
                        <EditAddressIcon />
                      </Button>
                      {menuSelected === "sub-zones" && (
                        <>
                          <Button
                            className={"btn-action" + (locationClicked.isStart ? " btn-primary" : "")}
                            onClick={() =>
                              handleClickAction(locationClicked, "set_start_address")
                            }
                          >
                            Start
                          </Button>
                          <Button
                            className={"btn-action" + (locationClicked.isEnd ? " btn-primary" : "")}
                            onClick={() =>
                              handleClickAction(locationClicked, "set_end_address")
                            }
                          >
                            Stopp
                          </Button>
                        </>
                      )}
                      {/* <Button
                        className="btn-action"
                        onClick={() =>
                          handleClickAction(locationClicked, "enter_latlng")
                        }
                      >
                        Redigera Lat/long
                        <EditLatLngIcon />
                      </Button> */}
                    </div>
                  </>
                )}
              </div>
            </Popup>
          )}
          {menuSelected === "sub-zones" && (
            <>
              {showDraw && (
                <DrawControl
                  position="top-left"
                  displayControlsDefault={false}
                  defaultMode="draw_polygon"
                  onCreate={onCreate}
                  onUpdate={onCreate}
                  onDelete={onCreate}
                  initialPoints={initialPoints}
                />
              )}
              <div className="ToolbarDraw">
                <div className="mapboxgl-ctrl-group button-group--horizontal">
                  <button
                    className={`mapbox-ctrl-draw-btn ${
                      !showDraw ? "active" : ""
                    }`}
                    onClick={() => handleShowDraw(false)}
                  >
                    <PanToolIcon />
                  </button>
                  <button
                    className={`mapbox-ctrl-draw-btn ${
                      showDraw ? "active" : ""
                    }`}
                    onClick={() => handleShowDraw(true)}
                  >
                    <PolylineIcon />
                  </button>
                </div>
              </div>
            </>
          )}
          <NavigationControl style={navControlStyle} />
          <button className="change-style-map" onClick={changeStyleMap}>
            <SyncOutlined />
          </button>
        </Map>
        <Modal
          open={state.visibleModal}
          onCancel={handleCancel}
          footer={[]}
          width={408}
        >
          <LocationForm
            zone={zone}
            circle={circle}
            form={form}
            state={state}
            isFetching={isFetching}
            handleCancel={handleCancel}
            onClosePopup={onClosePopup}
          />
        </Modal>
        <Modal
          open={stateSub.visibleModal}
          onCancel={handleCancelSub}
          footer={[]}
          width={408}
        >
          <SubZoneForm
            zone={zone}
            form={formSub}
            state={stateSub}
            isFetching={isFetchingSub}
            handleCancel={handleCancelSub}
            onClosePopup={onClosePopup}
            editPoints={editPoints}
          />
        </Modal>
        <Modal
          open={locationClicked && isConfirmedAddress}
          onCancel={onClosePopup}
          footer={[]}
          width={816}
          // GF-63: destroyOnClose is load-bearing, not tidiness. antd keeps the
          // modal body mounted after close (rc-dialog defaults destroyOnClose to
          // false), so ConfirmAddress kept its useState between openings — for a
          // DIFFERENT address row. The "nothing is preselected" guarantee then
          // held only for the first row of a mounting, while the normal workflow
          // is to work through the "?" rows one after another: row B would open
          // with row A's choice still selected and both buttons already enabled.
          // Unmounting on close makes every opening start from a clean state,
          // and also retires a stale-memo latency in markerCoord, whose deps do
          // not include locationOptions.
          destroyOnClose
        >
          <ConfirmAddress
            location={locationClicked}
            zone={zone}
            circle={circle}
            isFetching={isFetching}
            onClosePopup={onClosePopup}
          />
        </Modal>
      </div>
      {showDraw && (
        <div className="RightSubZoneForm">
          <SubZoneForm
            zone={zone}
            form={formSub}
            state={stateSub}
            stopsInNewZones={stopsInNewZones}
            isFetching={isFetchingSub}
            handleCancel={handleCancelSub}
            onClosePopup={onClosePopup}
          />
        </div>
      )}
    </>
  );
};

export default MapboxMain;
